/**
 * Disappearing Text — generate progressive cloze slides from a source paragraph.
 * Slide 0 = full text; later slides gap more words. Gaps are progressive (once
 * gapped, a word stays gapped on later slides).
 */

export const DISAPPEARING_SLIDE_COUNT = 5;

/** Fraction of gap-able words hidden per slide (index 0 = full text). */
export const DISAPPEARING_DENSITIES = [0, 0.22, 0.4, 0.62, 0.85];

const GOLDEN = 0.618033988749895;

/**
 * @param {string} [body]
 * @returns {{ text: string, image: string, imageWidth: number }}
 */
export function parseDisappearingTextBody(body) {
    if (!body) return { text: '', image: '', imageWidth: 50 };
    const trimmed = String(body).trim();
    if (trimmed.startsWith('{')) {
        try {
            const parsed = JSON.parse(trimmed);
            if (parsed && typeof parsed === 'object') {
                return {
                    text: typeof parsed.text === 'string' ? parsed.text : '',
                    image: typeof parsed.image === 'string' ? parsed.image : '',
                    imageWidth: Number(parsed.imageWidth) > 0 ? Number(parsed.imageWidth) : 50,
                };
            }
        } catch {
            /* fall through — treat as plain text */
        }
    }
    return { text: body, image: '', imageWidth: 50 };
}

/**
 * @param {{ text?: string, image?: string, imageWidth?: number }} config
 * @returns {string}
 */
export function serializeDisappearingTextBody(config) {
    return JSON.stringify({
        text: config?.text || '',
        image: config?.image || '',
        imageWidth: Number(config?.imageWidth) > 0 ? Number(config.imageWidth) : 50,
    });
}

/**
 * Split source into word / non-word tokens. Words may include internal apostrophes.
 * @param {string} text
 * @returns {{ type: 'word'|'other', value: string }[]}
 */
export function tokenizeDisappearingText(text) {
    const src = text || '';
    const tokens = [];
    const re = /([A-Za-zÀ-ÿ]+(?:'[A-Za-zÀ-ÿ]+)?)|([^A-Za-zÀ-ÿ]+)/g;
    let m;
    while ((m = re.exec(src)) !== null) {
        if (m[1]) tokens.push({ type: 'word', value: m[1] });
        else if (m[2]) tokens.push({ type: 'other', value: m[2] });
    }
    return tokens;
}

function gapScore(wordIndex) {
    return ((wordIndex + 1) * GOLDEN) % 1;
}

/**
 * Build one slide’s parts for a given density.
 * @param {{ type: 'word'|'other', value: string }[]} tokens
 * @param {number} density 0–1
 * @returns {{ type: 'text'|'gap', value: string }[]}
 */
export function buildDisappearingSlideParts(tokens, density) {
    const parts = [];
    let wordIndex = 0;
    let textBuf = '';

    const flushText = () => {
        if (textBuf) {
            parts.push({ type: 'text', value: textBuf });
            textBuf = '';
        }
    };

    for (const tok of tokens) {
        if (tok.type !== 'word') {
            textBuf += tok.value;
            continue;
        }
        const idx = wordIndex++;
        const gap = density > 0 && gapScore(idx) < density;
        if (gap) {
            flushText();
            parts.push({ type: 'gap', value: tok.value });
        } else {
            textBuf += tok.value;
        }
    }
    flushText();
    return parts;
}

/**
 * @param {string} text
 * @param {number} [slideCount]
 * @param {number[]} [densities]
 * @returns {{ type: 'text'|'gap', value: string }[][]}
 */
export function generateDisappearingPages(text, slideCount = DISAPPEARING_SLIDE_COUNT, densities = DISAPPEARING_DENSITIES) {
    const tokens = tokenizeDisappearingText(text || '');
    const pages = [];
    for (let i = 0; i < slideCount; i++) {
        const density = densities[i] ?? densities[densities.length - 1] ?? 0;
        pages.push(buildDisappearingSlideParts(tokens, density));
    }
    return pages;
}

/** Visual blank shown before a gap is revealed (matches teacher examples). */
export function disappearingBlankForWord(word) {
    const len = Math.max(5, Math.min(12, (word || '').length || 5));
    return '_'.repeat(len);
}
